import React, {useState, useRef, useEffect} from 'react';
import styled from "styled-components";
import {PageWrapper} from "../../../../components/UI/PageWrapper/PageWrapper";
import {CustomCard} from "../../../../components/UI/AntCustom/CustomCard";
import {CustomButton} from "../../../../components/UI/AntCustom/CustomButton";
import {useIntl} from "react-intl";
import {defineMessage, defineMessages} from "react-intl";
import {BigSearchInput} from "../../../../components/UI/AntCustom/BigSearchInput";
import {SearchOutlined} from "@ant-design/icons";
import DeleteIcon from "../../../../media/icons/delete.svg?react";
import {CommonButtonMessages} from "../../../../intl/commonMessages/CommonButtonMessages";
import {useIsMobile} from "../../../../hooks/useWindowSize";
import {mediaScreenSizes} from "../../../../utils/constans/styles/screenSizes";
import {CreateMessageTemplateModal} from "./components/CreateMessageTemplateModal";
import {DeleteMessageTemplateModal} from "./components/DeleteMessageTemplateModal";
import {useAppDispatch, useAppSelector} from "../../../../store/store";
import {
  clearMessageTemplateList,
  deleteMessageTemplate,
  getMessageTemplateList
} from "../../../../store/reducers/messageTemplatesReducer/messageTemplatesReducer";
import {InitialPage} from "../../../../utils/constans/pagination/pagination";
import {Loader} from "../../../../components/UI/Loader/Loader";
import {CustomResult} from "../../../../components/UI/AntCustom/CustomResult";
import {CommonFormMessages} from "../../../../intl/commonMessages/CommonFormMessages";
import {Tooltip} from "antd";
import {parseStringAsUTCDate} from "../../../../utils/helpers/dateHelpers/dateHelpers";
import {formatDate} from "../../../../utils/helpers/dateHelpers/dateHelpers";

const MessageTemplatesPageTitleMessage = defineMessage({
  id: "messageTemplatesPage.title",
  defaultMessage: "Message Templates",
})

const MessageTemplatesPageMessages = defineMessages({
  addTemplate: {
    id: "messageTemplatesPage.addTemplate",
    defaultMessage: "Add template",
  },
  found: {
    id: "messageTemplatesPage.found",
    defaultMessage: "Найденные шаблоны",
  },
  showFull: {
    id: "messageTemplatesPage.showFull",
    defaultMessage: "Показать полностью",
  },
  collapse: {
    id: "messageTemplatesPage.collapse",
    defaultMessage: "Свернуть",
  },
})

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;

  @media(max-width: ${mediaScreenSizes.mobile}) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const FoundText = styled.div`
  font-size: 14px;
  color: #808080;
  line-height: 24px;
`;

const SearchContainer = styled.div`
  @media(max-width: ${mediaScreenSizes.mobile}) {
    width: 100%;
  }
`;

const TemplatesGrid = styled.div<{ $isMobile: boolean }>`
  display: grid;
  grid-template-columns: ${props => props.$isMobile ? '1fr' : '1fr 1fr'};
  gap: 16px;
  width: 100%;
  
  > * {
    width: 100%;
    min-width: 0;
  }
  
  .ant-card-head-title {
    min-width: 0;
    flex: 1;
    overflow: hidden;
    
    > * {
      min-width: 0;
    }
  }
  
  .ant-card-extra {
    flex-shrink: 0;
  }
`;

const TemplateCardTitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
  overflow: hidden;
  width: 100%;
`;

const TemplateCardTitleText = styled.div`
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
  min-width: 0;
`;

const TemplateCardDate = styled.div`
  color: #000;
  opacity: .5;
  letter-spacing: -0.12px;
  font-size: 12px;
`;

const TemplateCardActions = styled.div`
  display: flex;
  gap: 8px;
`;

const EditButton = styled(CustomButton)`
  height: 35px;
`;

const DeleteButton = styled(CustomButton)`
  min-width: 45px;
  width: 45px;
  height: 35px;
  background: #FAFAFA;
`;

const TemplateCardText = styled.div<{ $isExpanded: boolean }>`
  font-size: 14px;
  line-height: 18px;
  color: #6B6B6B;
  
  ${props => !props.$isExpanded && `
    max-height: 90px;
    overflow: hidden;
  `}
`;

const EllipsisContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const EllipsisDots = styled.div`
  color: #6B6B6B;
  font-size: 14px;
  line-height: 10px;
  margin-bottom: 10px;
`;

const ShowFullButtonWrapper = styled.div`
`;

const TriangleIcon = styled.div<{ $isUp?: boolean }>`
  width: 0;
  height: 0;
  border-left: 4px solid transparent;
  border-right: 4px solid transparent;
  border-top: ${props => props.$isUp ? 'none' : '5px solid #1890FF'};
  border-bottom: ${props => props.$isUp ? '5px solid #1890FF' : 'none'};
`;

const ShowFullButton = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  color: #1890FF;
  font-size: 14px;
  cursor: pointer;
  user-select: none;
  
  &:hover {
    opacity: 0.8;
  }
`;

interface ITemplateData {
  id?: number;
  name: string;
  text: string;
}

export const MessageTemplatesPage = () => {
  const intl = useIntl();
  const isMobile = useIsMobile();
  const dispatch = useAppDispatch();
  const [isAddTemplateModalOpen, setIsAddTemplateModalOpen] = useState<boolean>(false);
  const [editingTemplate, setEditingTemplate] = useState<ITemplateData | undefined>(undefined);
  const [deletingTemplate, setDeletingTemplate] = useState<{ id: number; name: string } | undefined>(undefined);
  const [currentPage, setCurrentPage] = useState<number>(InitialPage);
  const [searchValue, setSearchValue] = useState<string>("");
  const [expandedTemplates, setExpandedTemplates] = useState<Set<number>>(new Set());
  const textRefs = useRef<Map<number, HTMLDivElement>>(new Map());

  const {
    items: templates,
    isLoading,
    totalCount: templatesCount,
  } = useAppSelector(state => state.messageTemplates.itemsObject);

  useEffect(() => {
    dispatch(getMessageTemplateList({
      page: currentPage,
      name: searchValue || undefined,
    }));

    return () => {
      dispatch(clearMessageTemplateList());
    }
  }, [currentPage, searchValue]);

  const formatTemplateDate = (dateString: string): string => {
    try {
      const date = parseStringAsUTCDate(dateString);
      return formatDate(date, 0, 'datetime_short_year');
    } catch {
      return dateString;
    }
  };

  const toggleExpand = (templateId: number) => {
    setExpandedTemplates(prev => {
      const newSet = new Set(prev);
      if (newSet.has(templateId)) {
        newSet.delete(templateId);
      } else {
        newSet.add(templateId);
      }
      return newSet;
    });
  };

  const [needsExpand, setNeedsExpand] = useState<Map<number, boolean>>(new Map());

  const checkTextHeight = (templateId: number, element: HTMLDivElement) => {
    requestAnimationFrame(() => {
      // Получаем актуальное состояние раскрытия
      const isExpanded = expandedTemplates.has(templateId);
      
      // Сохраняем текущие инлайн-стили элемента
      const originalMaxHeight = element.style.maxHeight;
      const originalOverflow = element.style.overflow;
      
      // Временно убираем ограничение высоты для измерения полной высоты контента
      element.style.maxHeight = 'none';
      element.style.overflow = 'visible';
      
      // Измеряем полную высоту контента через scrollHeight
      const fullHeight = element.scrollHeight;
      
      // Возвращаем стили обратно (только если текст не раскрыт)
      if (!isExpanded) {
        // Восстанавливаем ограничение или устанавливаем по умолчанию
        element.style.maxHeight = originalMaxHeight || '90px';
        element.style.overflow = originalOverflow || 'hidden';
      } else {
        // Если текст раскрыт, убираем ограничения через styled-components
        element.style.maxHeight = '';
        element.style.overflow = '';
      }
      
      // Высота 5 строк = line-height (18px) * 5 = 90px
      const maxHeight = 18 * 5;
      const needsExpandButton = fullHeight > maxHeight;
      
      // Обновляем состояние только если значение изменилось
      setNeedsExpand(prev => {
        const currentValue = prev.get(templateId);
        if (currentValue === needsExpandButton) {
          return prev; // Не обновляем, если значение не изменилось
        }
        const newMap = new Map(prev);
        newMap.set(templateId, needsExpandButton);
        return newMap;
      });
    });
  };

  const setTextRef = (templateId: number) => (el: HTMLDivElement | null) => {
    if (el) {
      textRefs.current.set(templateId, el);
      checkTextHeight(templateId, el);
    } else {
      textRefs.current.delete(templateId);
    }
  };

  useEffect(() => {
    const handleResize = () => {
      textRefs.current.forEach((el, templateId) => {
        checkTextHeight(templateId, el);
      });
    };
    
    window.addEventListener('resize', handleResize);
    
    // Проверяем высоту при изменении состояния раскрытия или шаблонов
    textRefs.current.forEach((el, templateId) => {
      checkTextHeight(templateId, el);
    });
    
    return () => window.removeEventListener('resize', handleResize);
  }, [templates, expandedTemplates]);

  const handleOpenCreateModal = () => {
    setEditingTemplate(undefined);
    setIsAddTemplateModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsAddTemplateModalOpen(false);
    setEditingTemplate(undefined);
  };

  const handleSuccess = () => {
    dispatch(getMessageTemplateList({
      page: currentPage,
      name: searchValue || undefined,
    }));
  };

  const handleDelete = (templateId: number, templateName: string) => {
    setDeletingTemplate({ id: templateId, name: templateName });
  };

  const handleDeleteSuccess = () => {
    dispatch(getMessageTemplateList({
      page: currentPage,
      name: searchValue || undefined,
    }));
  };

  const addTemplateButton = (
    <CustomButton
      heightSize={isMobile ? 's' : 'm'}
      type={'primary'}
      onClick={handleOpenCreateModal}
    >
      + {intl.formatMessage(MessageTemplatesPageMessages.addTemplate)}
    </CustomButton>
  );

  return (
    <PageWrapper>
      <CustomCard 
        title={intl.formatMessage(MessageTemplatesPageTitleMessage)}
        width={'max'}
        isContainerCard={true}
        extra={addTemplateButton}
        titlePosition={'space-between'}
      >
        <HeaderRow>
          <FoundText>
            {intl.formatMessage(MessageTemplatesPageMessages.found)}({templatesCount})
          </FoundText>
          <SearchContainer>
            <BigSearchInput
              placeholder={intl.formatMessage({ id: 'messageTemplatesPage.searchPlaceholder', defaultMessage: 'Поиск' })}
              allowClear
              value={searchValue}
              prefix={<SearchOutlined />}
              onChange={(e) => setSearchValue(e.target.value)}
            />
          </SearchContainer>
        </HeaderRow>
        {isLoading ? (
          <Loader />
        ) : templates.length === 0 ? (
          <CustomResult title={intl.formatMessage(CommonFormMessages.noData)} />
        ) : (
          <TemplatesGrid $isMobile={isMobile}>
            {templates.map(t => (
              <CustomCard
                key={t.id}
                width={'max'}
                style={{ width: '100%' }}
                title={
                  <TemplateCardTitleWrapper>
                    <TemplateCardTitleText>{t.name}</TemplateCardTitleText>
                    <TemplateCardDate>{formatTemplateDate(t.dateModified || t.dateAdded)}</TemplateCardDate>
                  </TemplateCardTitleWrapper>
                }
                isContainerCard={true}
                titlePosition={'space-between'}
                rowGap={4}
                extra={
                  <TemplateCardActions>
                    <EditButton
                      type={'primary'}
                      heightSize={'m'}
                      onClick={() => {
                        setEditingTemplate({
                          id: t.id,
                          name: t.name,
                          text: t.text,
                        });
                        setIsAddTemplateModalOpen(true);
                      }}
                    >
                      {intl.formatMessage(CommonButtonMessages.edit)}
                    </EditButton>
                    <Tooltip title={intl.formatMessage(CommonButtonMessages.delete)}>
                      <DeleteButton
                        heightSize={'m'}
                        icon={<DeleteIcon />}
                        onClick={() => handleDelete(t.id, t.name)}
                      />
                    </Tooltip>
                  </TemplateCardActions>
                }
              >
                <TemplateCardText 
                  ref={setTextRef(t.id)}
                  $isExpanded={expandedTemplates.has(t.id)}
                >
                  {t.text}
                </TemplateCardText>
              {needsExpand.get(t.id) && (
                <>
                  {!expandedTemplates.has(t.id) && (
                    <EllipsisContainer>
                      <EllipsisDots>...</EllipsisDots>
                      <ShowFullButton onClick={() => toggleExpand(t.id)}>
                        <span>{intl.formatMessage(MessageTemplatesPageMessages.showFull)}</span>
                        <TriangleIcon />
                      </ShowFullButton>
                    </EllipsisContainer>
                  )}
                  {expandedTemplates.has(t.id) && (
                    <ShowFullButtonWrapper>
                      <ShowFullButton onClick={() => toggleExpand(t.id)}>
                        <span>{intl.formatMessage(MessageTemplatesPageMessages.collapse)}</span>
                        <TriangleIcon $isUp={true} />
                      </ShowFullButton>
                    </ShowFullButtonWrapper>
                  )}
                </>
              )}
              </CustomCard>
            ))}
          </TemplatesGrid>
        )}
      </CustomCard>

      {isAddTemplateModalOpen && (
        <CreateMessageTemplateModal 
          isModalVisible={isAddTemplateModalOpen} 
          setIsModalVisible={handleCloseModal}
          templateData={editingTemplate}
          onSuccess={handleSuccess}
        />
      )}

      {deletingTemplate && (
        <DeleteMessageTemplateModal
          templateId={deletingTemplate.id}
          templateName={deletingTemplate.name}
          isModalVisible={!!deletingTemplate}
          setIsModalVisible={(visible) => {
            if (!visible) {
              setDeletingTemplate(undefined);
            }
          }}
          onSuccess={handleDeleteSuccess}
        />
      )}
    </PageWrapper>
  );
};

